import React, { useState, useEffect, useMemo } from "react";
import axios from 'axios';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PersonIcon from '@mui/icons-material/Person';
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import { Box, Typography, Drawer, IconButton, useMediaQuery, List,
   ListItem, ListItemIcon, ListItemText, Collapse, Tooltip } from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { ExpandMore, ChevronRight, MenuOutlined, HowToReg, ExitToApp } from "@mui/icons-material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import GroupAdd from "@mui/icons-material/GroupAdd";
import { CreateNewFolder, AssignmentTurnedIn, HourglassEmpty, CheckCircle,
  Add, Visibility } from "@mui/icons-material";
import { OrganizationsCompaniesprofile } from '../../../helper/base_url';
import { isTokenExpired } from "../../../utils/tokenUtils";
import { jwtDecode } from "jwt-decode";

const SidebarComponent = () => {
  const [selected, setSelected] = useState("Pending Projects");
  const [expandedMenu, setExpandedMenu] = useState("Post Projects");
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [formData, setFormData] = useState({});
  const [ , setLoading] = useState(false);
  const [, setError] = useState(null);

  const isDesktop = useMediaQuery("(min-width : 1024px)");
  const navigate = useNavigate();

  const logout = () => {
    localStorage.removeItem("company_token");
    window.location.href = "https://site.huebits.in/organizations";
  };

  useEffect(() => {
    const token = localStorage.getItem("company_token");

    if (!token || isTokenExpired(token)) {
      logout();
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const expiryTime = decoded.exp * 1000;
      const currentTime = Date.now();
      const timeout = expiryTime - currentTime;

      const timer = setTimeout(() => {
        logout(); // Auto logout when token expires
      }, timeout);

      return () => clearTimeout(timer);
    } catch (err) {
      logout();
      return;
    }
  }, []);
  
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${OrganizationsCompaniesprofile}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('company_token')}`,
          },
        });
        setFormData(response.data.data);
      } catch (err) {
        //console.error('Error fetching profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);
  
  useEffect(() => {
    setIsSidebarVisible(true);
  }, [isDesktop]);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("company_token");
    if (token) {
      localStorage.setItem("company_token", token);
      setSelected("Pending Projects");
      navigate("/organizations/dashboard/pending-project");
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("company_token");
    if (!token) 
    window.location.href = "https://site.huebits.in/organizations";
  }, [navigate]);

  const toggleMenu = (menuTitle) => {
    setExpandedMenu((prev) => (prev === menuTitle ? "" : menuTitle));
  };

  const menuItems = useMemo(
    () => [
      {
        title: "Post Projects",
        icon: <AssignmentTurnedInIcon />,
        children: [
          {
            label: "Post New Project",
            icon: <CreateNewFolder />,
            onClick: () => navigate("/organizations/new-post-project"),
          },
          {
            label: "Pending Projects",
            icon: <HourglassEmpty />,
            onClick: () => navigate("/organizations/dashboard/pending-project"),
          },
          {
            label: "Active Projects",
            icon: <AssignmentTurnedIn />,
            onClick: () => navigate("/organizations/dashboard/active-project"),
          },
          {
            label: "Completed Projects",
            icon: <CheckCircle />,
            onClick: () => navigate("/organizations/dashboard/completed-project"),
          },
        ],
      },
      {
        title: "Hire Tech Talent",
        icon: <GroupAdd />,
        children: [
          {
            label: "Hire New Talent",
            icon: <Add />,
            onClick: () => navigate("/organizations/hire-tech-talent"),
          },
          {
            label: "My Job Postings",
            icon: <Visibility />,
            onClick: () => navigate("/organizations/dashboard/hire-tech-data"),
          },
          {
            label: "Hired Users",
            icon: <HowToReg />,
            onClick: () => navigate("/organizations/dashboard/hire-tech-hired-users"),
          },
        ],
      },
      {
        title: "Profile",
        icon: <PersonIcon />,
        onClick: () => navigate("/organizations/dashboard/profile"),
      },
      {
        title: "Ideavize AI",
        icon: <BusinessCenterOutlinedIcon />,
        onClick: () => navigate("/organizations/dashboard/ideavize-ai"),
      },
    ],
    [navigate]
  );

  const SidebarItem = ({ title, icon, children, onClick }) => {
    const isExpanded = expandedMenu === title;

    const handleItemClick = () => {
      if (children) toggleMenu(title);
      else {
        onClick?.();
        setSelected(title);
        if (!isDesktop) setIsSidebarVisible(false);
      }
    };

    return (
      <>
        <ListItem
          // button
          onClick={handleItemClick}
          selected={selected === title}
          sx={{
            px: 3,
            bgcolor: selected === title ? "#ffff" : "transparent",
            // borderLeft : selected === title ? "4px solid #0e68f9" : "4px solid transparent",
            borderLeft : selected === title ? "4px solidrgb(255, 255, 255)" : "4px solid transparent",
            // "&:hover": {
            //   bgcolor: "#052659",
            // },
          }}
        >
        <ListItemIcon sx={{ color: selected === title ? "#052659" : "#ffff" }}>
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={title}
            primaryTypographyProps={{
              fontWeight: selected === title ? "bold" : 500,
              fontSize: "16px",
              color: selected === title ? "#052659" : "#ffff",
            }}
          />
          {children && (isExpanded ? <ExpandMore /> : <ChevronRight />)}
        </ListItem>

        {children && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((child, idx) => (
                <ListItem
                  key={idx}
                  // button
                  onClick={() => {
                    child.onClick?.();
                    setSelected(child.label);
                    if (!isDesktop) setIsSidebarVisible(false);
                  }}
                  selected={selected === child.label}
                  sx={{
                    pl: 6,
                    bgcolor: selected === child.label ? "#ffff" : "transparent",
                    borderLeft : selected === child.label ? "4px solidrgb(255, 255, 255)" : "4px solid transparent",
                    // "&:hover": {
                    //   bgcolor: "rgb(21, 45, 83)",
                    // },
                  }}
                >
                <ListItemIcon sx={{ color: selected === child.label ? "#052659" : "#fff" }}>
                  {child.icon}
                </ListItemIcon>
                  <ListItemText
                    primary={child.label}
                    primaryTypographyProps={{
                      fontWeight: selected === child.label ? "bold" : 500,
                      fontSize: "15px",
                      //color: selected === child.label ? "#fff" : "#052659", // <- Here
                      color: selected === child.label ? "#052659" : "#fff",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  };

  return (
    <>
      {/* Top Bar */}
      <Box
        sx={{
          position: "fixed",
          top : 0,
          left : 0,
          right : 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgb(1, 19, 37)",
          color: "#fff",
          zIndex: 1300,
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography
          variant="h6"
          component={Link}
          to="#"
          sx={{
            textDecoration: "none",
            color: "#fff",
            fontWeight: "bold",
            fontSize: "24px",
            marginLeft : "80px",
          }}
        >
          Hue<span style={{ color: "#0e68f9" }}>bits</span>
        </Typography>
        {/* Right-side Section with Profile and Logout */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginRight :"60px" }}>
          {/* Logout Button */}
          <Tooltip title="Logout">
            <IconButton onClick={logout} sx={{ color: "#fff" }}>
              <ExitToApp />
            </IconButton>
          </Tooltip>

          {/* Profile Picture */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              cursor: "pointer",
            }}
          >
            {formData.profilePic ? (
              <img
                src={formData.profilePic}
                alt="Profile"
                style={{
                  width : "40px",
                  height : "40px",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            ) : (
              <PersonOutlinedIcon sx={{ color: "#4081c2", fontSize: "30px" }} />
            )}
          </Box>
        </Box>
      </Box>
      {/* Sidebar */}
      <Drawer
        anchor="left"
        open={isSidebarVisible}
        variant={isDesktop ? "persistent" : "temporary"}
        onClose={() => setIsSidebarVisible(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width : 260,
            boxSizing: "border-box",
            backgroundColor: "#0e68f9",
            borderRight : "1px solid #e0e0e0",
            color:"#ffff",
            paddingTop : 8,
            height : "100vh",
            overflowY: "auto",
            position: "relative",
            zIndex: 1200,
          },
        }}
      >
        <List>
          {menuItems.map((item, idx) => (
            <SidebarItem
              key={idx}
              title={item.title}
              icon={item.icon}
              onClick={item.onClick}
            >
              {item.children}
            </SidebarItem>
          ))}
        </List>
      </Drawer>

      {/* Mobile Sidebar Open Button */}
      {!isSidebarVisible && !isDesktop && (
        <IconButton
          onClick={() => setIsSidebarVisible(true)}
          sx={{
            position: "fixed",
            top : "10px",
            left : "10px",
            backgroundColor: "#0e68f9",
            color: "#fff",
            zIndex: 1300,
            "&:hover": {
              backgroundColor: "#052659",
            },
          }}
        >
          <MenuOutlined />
        </IconButton>
      )}
    </>
  );
};

export default SidebarComponent;
